import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { DvButtonComponent, DvDialogCloseEvent, DvNumberInputComponent } from '@devinco/ui/dds';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { ActiveVippsUIStatus } from '@shared/interfaces/vipps';
import { VippsStatusComponent } from './vipps-status/vipps-status.component';
import { DialogService } from '@core/services/dialog.service';
import { of } from 'rxjs';

declare global {
  interface Window {
    VippsCheckoutButton: any;
  }
}

@Component({
  selector: 'app-vipps',
  imports: [DvButtonComponent, TranslatePipe, DvNumberInputComponent, VippsStatusComponent],
  templateUrl: './vipps.component.html',
  styleUrl: './vipps.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VippsComponent implements AfterViewInit {
  private snackbar = inject(DvSnackbarService);
  private dialog = inject(DialogService);
  readonly translate = inject(TranslateService);

  status = input<ActiveVippsUIStatus>(undefined);

  donate = output<number>();
  cancelDonation = output<void>();

  amounts = signal<number[]>([50, 100, 200]).asReadonly();

  amount50Selected = signal<boolean>(false);
  amount100Selected = signal<boolean>(true);
  amount200Selected = signal<boolean>(false);
  amountCustomSelected = signal<boolean>(false);

  customAmount = signal<number | null>(null);

  selectedAmount = signal<number | undefined>(100);

  protected container = viewChild<any>('vippsButtonContainer');

  ngAfterViewInit(): void {
    this.waitForVipps(() => {
      window.VippsCheckoutButton.render({
        container: this.container().nativeElement,
        language: this.translate.getCurrentLang().toLowerCase(),
      });
    });
  }

  donateClicked(): void {
    const selectedAmount = this.selectedAmount();

    if (selectedAmount) {
      this.donate.emit(selectedAmount);
      return;
    }

    const customAmount = this.customAmount();
    if (selectedAmount === 0 && customAmount && customAmount > 0) {
      (customAmount >= 600
        ? this.dialog.open({
            title: 'Vipps.ConfirmDonationDialogTitle',
            type: 'warning',
            cancelText: 'General.Cancel',
            confirmText: 'General.Proceed',
            contentText: 'Vipps.ConfirmationDialogContent',
          }).closed
        : of({ source: 'confirm' } as DvDialogCloseEvent)
      ).subscribe(({ source }) => {
        if (source !== 'confirm') return;
        this.donate.emit(customAmount);
      });

      return;
    }

    this.snackbar.open(
      this.translate.instant('General.SelectAmountForDonation', SnackBarConfig.Warning),
    );
  }

  private waitForVipps(callback: () => void) {
    const interval = setInterval(() => {
      if (window.VippsCheckoutButton) {
        clearInterval(interval);
        callback();
      }
    }, 50);
  }

  amountSelected(amount: number | undefined): void {
    this.selectedAmount.set(amount);
  }
}
